import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { ClassSchedule } from './ClassSchedule';
import { SuspenseLiveComponent } from '../LiveComponent/SuspenseLiveComponent';
import './DeliveryDetails.scss';
import {
    ClassStatus,
    CourseProps,
    daysUntilStart,
    getRemainingSeats,
    LearningActivityStatus,
    RegistrationStatus,
    TranscriptStatus,
    isWithinClassStartTimeAndEndTime,
    isAfterClassEndTime,
    getNodeEnvironment,
} from '../../common';
import {
    shouldShowClassroomButton,
    shouldShowJoinWebExTrainingButton,
    shouldShowEnrollButton,
    shouldShowWithdrawButton,
    shouldShowWaitlistButton,
    shouldShowUnwaitlistButton,
    shouldShowEvaluationButton,
    shouldShowCertificateButton,
    shouldShowJoinInstruction,
    shouldShowRequestAccessButton,
    shouldShowRequestAccessPending,
    shouldShowRequestAccessApproved,
    shouldShowRequestAccessRejected,
} from '../../common/buttonUtils';
import {
    useRegisterLearningActivity,
    useUnwaitlistLearningActivity,
    useWaitlistLearningActivity,
    useWithdrawLearningActivity,
} from '../../hooks/registration';
import { useCreateUserRequest } from '../../hooks/requests';
import { NotificationContext } from '../notifications/NotificationContext';
import { CourseContext } from '../course/CourseContext';
import { FlagNames, getErrorMessageFromErrorCode, isFeatureEnabled, Stages } from '@amzn/aws-tc-ilt-common';
import { useLazyCertificate } from '../../hooks/certificate';
import { useAuthContext } from '../auth/AuthContext';
import { useTrackUserEvent } from '../../common/telemetry';
import { TelemetryEvents, TelemetryPage } from '../../common/TelemetryEvents';
import { TELEMETRY_EVENT_TYPES } from '../../common';

/** Shorter than the budget for the remote's first load: by now it is mounted and doing one re-read. */
export const CLASS_CONTENT_SETTLE_TIMEOUT_MS = 3_000;

type RegistrationAction = 'enroll' | 'withdraw';

const AWAITED_CLASS_CONTENT: Record<RegistrationAction, boolean> = { enroll: true, withdraw: false };

export interface DeliveryDetailsProps {
    course: CourseProps;
    page: TelemetryPage;
    showLocationHeading?: boolean;
    refetchCourses?: () => void;
    orgId?: string;
    /**
     * Set only for a guides-only class, which is the one case the header guide buttons render in.
     * The caller already knows this from the XPS, so the mount is skipped rather than made and
     * left empty — an empty entry would still count toward the row's button total.
     */
    showGuideButtons?: boolean;
    /** `undefined` until the XPS has answered, which is distinct from a class with no tab. */
    hasClassContent?: boolean;
}

/**
 * Maps a createUserRequest rejection to user-facing copy. UCS sends its own codes, not
 * myClass ones (so `getErrorMessageFromErrorCode` would read "Unknown error"), and the
 * rejections mapped below all arrive as the same RESOURCE_INVALID_STATE code — the sentence
 * is the only thing that distinguishes them. The substrings below are coupled to
 * AwsTcLearningAccountService userRequestValidator messages; a reworded rejection falls
 * back to the generic message rather than mislabeling.
 */
const getRequestAccessErrorMessage = (errorMessage?: string): string => {
    if (errorMessage?.startsWith('REGEX_VALIDATION_ERROR')) {
        return 'This registration link is not valid. Ask the person who sent it for an updated link.';
    }
    if (errorMessage?.includes('full capacity')) {
        return 'This class is full. Contact your Learning Administrator.';
    }
    if (errorMessage?.includes('registration period has ended')) {
        return 'Registration for this class has closed. Contact your Learning Administrator.';
    }
    if (errorMessage?.includes('not a customer of this learning activity')) {
        return "This class isn't available to your organization. Contact your Learning Administrator.";
    }
    if (errorMessage?.includes('B2B user')) {
        return 'Request access is not available for accounts that already belong to an organization. Contact your Learning Administrator for help with this class.';
    }
    return 'Failed to request access for the class. Contact your Learning Administrator for help.';
};

export const DeliveryDetails = (props: DeliveryDetailsProps) => {
    const { showInfoFlashbar, showSuccessFlashbar, showErrorFlashbar } = useContext(NotificationContext);
    const { user, userRequests, refetchUser } = useAuthContext();
    const [callEnroll] = useRegisterLearningActivity(`${props.course.id}`);
    const [callWithdraw] = useWithdrawLearningActivity(`${props.course.id}`);
    const [callWaitlist] = useWaitlistLearningActivity(`${props.course.id}`);
    const [callUnwaitlist] = useUnwaitlistLearningActivity(`${props.course.id}`);
    const [getCertificate, { loading: loadingCertificate }] = useLazyCertificate();
    const [callCreateUserRequest] = useCreateUserRequest();
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState<{
        action: RegistrationAction;
        course: CourseProps;
        showGuideButtons?: boolean;
        settle: () => void;
    } | null>(null);
    const { updateCourseStatus } = useContext(CourseContext);
    const trackUserEvent = useTrackUserEvent();

    // Resolve telemetry event IDs based on which page this component is rendered on
    const isDashboard = props.page === TelemetryPage.DASHBOARD;
    const telemetry = {
        enroll: isDashboard ? TelemetryEvents.DASHBOARD_ENROLL_CLICK : TelemetryEvents.COURSE_DETAIL_ENROLL_CLICK,
        withdraw: isDashboard ? TelemetryEvents.DASHBOARD_WITHDRAW_CLICK : TelemetryEvents.COURSE_DETAIL_WITHDRAW_CLICK,
        waitlist: isDashboard ? TelemetryEvents.DASHBOARD_WAITLIST_CLICK : TelemetryEvents.COURSE_DETAIL_WAITLIST_CLICK,
        unwaitlist: isDashboard ? TelemetryEvents.DASHBOARD_UNWAITLIST_CLICK : TelemetryEvents.COURSE_DETAIL_UNWAITLIST_CLICK,
        certificate: isDashboard ? TelemetryEvents.DASHBOARD_CERTIFICATE_CLICK : TelemetryEvents.COURSE_DETAIL_CERTIFICATE_CLICK,
        requestAccess: isDashboard ? TelemetryEvents.DASHBOARD_REQUEST_ACCESS_CLICK : TelemetryEvents.COURSE_DETAIL_REQUEST_ACCESS_CLICK,
        joinTraining: isDashboard ? TelemetryEvents.DASHBOARD_JOIN_TRAINING_CLICK : TelemetryEvents.COURSE_DETAIL_JOIN_TRAINING_CLICK,
        builderLabs: isDashboard ? TelemetryEvents.DASHBOARD_BUILDER_LABS_CLICK : TelemetryEvents.COURSE_DETAIL_BUILDER_LABS_CLICK,
        evaluationSurvey: isDashboard
            ? TelemetryEvents.DASHBOARD_EVALUATION_SURVEY_CLICK
            : TelemetryEvents.COURSE_DETAIL_EVALUATION_SURVEY_CLICK,
    };

    const isIltClassroomsXpsEnabled = isFeatureEnabled(FlagNames.MYCLASS_ILT_CLASSROOMS_XPS, getNodeEnvironment() as Stages);

    const features = {
        isTeamAuthorizationEnabled: true,
        hideJoinButtonForNonWebExCourse: true,
        privateLinkSharing: true,
        adcLinkSharing: true,
    };

    // Show flashbar when request access is approved
    useEffect(() => {
        const isRequestAccessApproved = shouldShowRequestAccessApproved(props.course, user, features, props.orgId, userRequests);
        if (isRequestAccessApproved) {
            showSuccessFlashbar(
                'Your request got approved! Please sign-out myClass now and sign-in again to view your enrolled class tile on your dashboard',
            );
        }
    }, [props.course, user, features, props.orgId, userRequests, showSuccessFlashbar]);

    // Show flashbar when request access is rejected
    useEffect(() => {
        const isRequestAccessRejected = shouldShowRequestAccessRejected(props.course, user, features, props.orgId, userRequests);
        if (isRequestAccessRejected) {
            showErrorFlashbar('Your request had rejected by your administrator. Please contact your administrator.');
        }
    }, [props.course, user, features, props.orgId, userRequests, showErrorFlashbar]);

    const downloadCertificate = () => {
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.certificate);
        getCertificate({ variables: { transcriptId: props.course.transcriptId } }).then((result) => {
            if (result.error) {
                showErrorFlashbar(getErrorMessageFromErrorCode(result.error.message));
            } else {
                const base64Pdf = `data:application/pdf;base64,${result.data.certificate}`;
                const fileName = 'Certificate.pdf';
                // Mock a download link to open the PDF in a new tab
                const mockDownloadLink = document.createElement('a');
                mockDownloadLink.href = base64Pdf;
                mockDownloadLink.download = fileName;
                mockDownloadLink.click();
            }
        });
    };

    // Only the course page renders the Class content tab, so only it has a tab to wait for.
    const tracksClassContent = !isDashboard && isIltClassroomsXpsEnabled;

    /** The XPS answers after the mutation resolves, so the note goes out last rather than first. */
    const settleWithClassContent = (action: RegistrationAction, before: CourseProps, announce: () => void) => {
        let settled = false;
        const settle = () => {
            if (settled) {
                return;
            }
            settled = true;
            setPending(null);
            setLoading(false);
            announce();
        };

        if (!tracksClassContent || props.hasClassContent === AWAITED_CLASS_CONTENT[action]) {
            settle();
            return;
        }
        // The XPS reports only changes, so a class whose content did not change never answers. The
        // timer is left to run: it outlives an unmount, and `settled` makes a late one a no-op.
        setTimeout(settle, CLASS_CONTENT_SETTLE_TIMEOUT_MS);
        setPending({ action, course: before, showGuideButtons: props.showGuideButtons, settle });
    };

    useEffect(() => {
        if (pending && props.hasClassContent === AWAITED_CLASS_CONTENT[pending.action]) {
            pending.settle();
        }
    }, [pending, props.hasClassContent]);

    const enroll = () => {
        const before = props.course;
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.enroll);
        setLoading(true);
        callEnroll().then((result) => {
            if (result.errors) {
                const errorMessage = (result.errors as unknown as { message: string }).message;
                showErrorFlashbar(getErrorMessageFromErrorCode(errorMessage));
                setLoading(false);
                return;
            }
            updateCourseStatus(props.course.id, RegistrationStatus.REGISTERED);
            if (props.refetchCourses) {
                props.refetchCourses();
            }
            settleWithClassContent('enroll', before, () =>
                showSuccessFlashbar(`Successfully registered for the class "${props.course.courseDetails.name}".`),
            );
        });
    };

    const withdraw = () => {
        const before = props.course;
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.withdraw);
        setLoading(true);
        callWithdraw().then((result) => {
            if (result.errors) {
                const errorMessage = (result.errors as unknown as { message: string }).message;
                showErrorFlashbar(getErrorMessageFromErrorCode(errorMessage));
                setLoading(false);
                return;
            }
            updateCourseStatus(props.course.id, RegistrationStatus.WITHDRAWN);
            settleWithClassContent('withdraw', before, () =>
                showInfoFlashbar(`Successfully withdrew from the class "${props.course.courseDetails.name}"`),
            );
        });
    };

    const waitlist = () => {
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.waitlist);
        setLoading(true);
        callWaitlist().then((result) => {
            if (result.errors) {
                const errorMessage = (result.errors as unknown as { message: string }).message;
                showErrorFlashbar(getErrorMessageFromErrorCode(errorMessage));
            } else {
                updateCourseStatus(props.course.id, RegistrationStatus.WAITLISTED);
                showSuccessFlashbar(`Successfully waitlisted for the class "${props.course.courseDetails.name}".`);
            }
            setLoading(false);
        });
    };

    const unwaitlist = () => {
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.unwaitlist);
        setLoading(true);
        callUnwaitlist().then((result) => {
            if (result.errors) {
                const errorMessage = (result.errors as unknown as { message: string }).message;
                showErrorFlashbar(getErrorMessageFromErrorCode(errorMessage));
            } else {
                updateCourseStatus(props.course.id, RegistrationStatus.UNWAITLISTED);
                showInfoFlashbar(`Successfully removed from the waitlist class "${props.course.courseDetails.name}"`);
            }
            setLoading(false);
        });
    };

    const requestAccess = () => {
        trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, telemetry.requestAccess);
        setLoading(true);
        callCreateUserRequest({
            variables: {
                organizationId: props.orgId,
                resourceId: `${props.course.id}`,
            },
        }).then((result) => {
            if (result.errors) {
                const errorMessage = (result.errors as unknown as { message: string }).message;
                showErrorFlashbar(getRequestAccessErrorMessage(errorMessage));
                setLoading(false);
            } else {
                showSuccessFlashbar(`Successfully requested access for the class "${props.course.courseDetails.name}"`);
                // Refetch user data to update the user request status
                refetchUser();
                setLoading(false);
            }
        });
    };

    const getClassStatusBasedOnTime: (course: CourseProps) => ClassStatus = (course) => {
        if (isWithinClassStartTimeAndEndTime(course)) {
            return ClassStatus.IN_PROGRESS;
        } else if (isAfterClassEndTime(course)) {
            return ClassStatus.COMPLETED;
        }
        return ClassStatus.ENROLLED;
    };

    const getClassStatus = (course: CourseProps) => {
        switch (course.courseStatus) {
            case LearningActivityStatus.COMPLETED:
                if (course.transcriptStatus === TranscriptStatus.NO_SHOW) return ClassStatus.NO_SHOW;
                if (course.transcriptStatus === TranscriptStatus.PARTIALLY_ATTENDED) return ClassStatus.PARTIALLY_ATTENDED;
                if (course.transcriptStatus === TranscriptStatus.ATTENDED) return ClassStatus.ATTENDED;
                if (course.registrationStatus === RegistrationStatus.WAITLISTED) return ClassStatus.COMPLETED_WAITLISTED;
                return ClassStatus.COMPLETED;
            case LearningActivityStatus.CANCELED:
                return ClassStatus.CANCELED;
            case LearningActivityStatus.ACTIVE:
                switch (course.registrationStatus) {
                    case RegistrationStatus.WAITLISTED:
                        return ClassStatus.WAITLISTED;
                    case RegistrationStatus.WITHDRAWN:
                    case RegistrationStatus.UNWAITLISTED:
                        return ClassStatus.WITHDRAWN;
                    case RegistrationStatus.REGISTERED:
                        return getClassStatusBasedOnTime(course);
                    case RegistrationStatus.CANCELED: // Shouldn't happen, as once LearningActivity is cancelled all the registration should be cancelled.
                        return ClassStatus.CANCELED;
                }
        }
    };

    /**
     * `updateCourseStatus` flips the registration and `refetchCourses` can land a new seat count
     * before the tab catches up, so the panel reads from one held value rather than announcing the
     * change one readout at a time.
     */
    const displayedCourse = pending?.course ?? props.course;
    const displayedShowGuideButtons = pending ? pending.showGuideButtons : props.showGuideButtons;

    const getStatusIndicator = (course: CourseProps) => {
        const classStatus = getClassStatus(course);
        switch (classStatus) {
            case ClassStatus.CANCELED:
                return (
                    <StatusIndicator type='error' data-testid='status-canceled'>
                        Canceled
                    </StatusIndicator>
                );
            case ClassStatus.NO_SHOW:
                return (
                    <StatusIndicator type='error' data-testid='status-no-show'>
                        No Show
                    </StatusIndicator>
                );
            case ClassStatus.PARTIALLY_ATTENDED:
                return (
                    <StatusIndicator type='info' data-testid='status-partially-attended'>
                        Partially Attended
                    </StatusIndicator>
                );
            case ClassStatus.ATTENDED:
                return <StatusIndicator data-testid='status-attended'>Attended</StatusIndicator>;
            case ClassStatus.COMPLETED:
                return <StatusIndicator data-testid='status-completed'>Completed</StatusIndicator>;
            case ClassStatus.IN_PROGRESS:
                return (
                    <StatusIndicator type='in-progress' data-testid='status-in-progress'>
                        In Progress
                    </StatusIndicator>
                );
            case ClassStatus.COMPLETED_WAITLISTED:
                return (
                    <StatusIndicator type='info' data-testid='status-waitlisted'>
                        Waitlisted
                    </StatusIndicator>
                );
            case ClassStatus.WAITLISTED:
                return (
                    <StatusIndicator type={course.schedule.seatsRemaining ? 'info' : 'stopped'} data-testid='status-info'>
                        {getRemainingSeats(course.schedule.seatsRemaining)}
                    </StatusIndicator>
                );
            case ClassStatus.ENROLLED: {
                const days = daysUntilStart(course.schedule.classDays[0].startDateTime);
                return (
                    <StatusIndicator type='pending' data-testid='status-enrolled'>
                        Enrolled. {days === 1 ? `1 day left` : days > 1 ? `${days} days left` : ''}
                    </StatusIndicator>
                );
            }
            case ClassStatus.WITHDRAWN:
                return (
                    <StatusIndicator type={course.schedule.seatsRemaining ? 'info' : 'stopped'} data-testid='status-info'>
                        {getRemainingSeats(course.schedule.seatsRemaining)}
                    </StatusIndicator>
                );
        }
    };

    const getButtons = (course: CourseProps) => {
        /**
         * Gated on the XPS rather than dropped outright: with the flag off there is no Class content
         * tab, and this link out to the Classrooms site is then the only route to labs and guides.
         */
        const classroomButton = shouldShowClassroomButton(course) && !isIltClassroomsXpsEnabled && (
            <Button
                href={course.classroomUrl}
                iconAlign='right'
                iconName='external'
                target='_blank'
                ariaLabel='Class Content'
                data-testid='button-aws-labs'
                className='classroom-link'
                onClick={() => trackUserEvent(TELEMETRY_EVENT_TYPES.LINK_CLICK, telemetry.builderLabs)}
            >
                Class Content
            </Button>
        );

        const joinTrainingButton = shouldShowJoinWebExTrainingButton(course, features) && (
            <Button
                variant='primary'
                href={course.meetingLink}
                iconAlign='right'
                iconName='external'
                target='_blank'
                ariaLabel='Join Training'
                disabled={!course.meetingLink}
                data-testid='button-join-training'
                onClick={() => trackUserEvent(TELEMETRY_EVENT_TYPES.LINK_CLICK, telemetry.joinTraining)}
            >
                Join Training
            </Button>
        );

        /**
         * Guide buttons for a guides-only class, which would otherwise have nothing but a lone
         * button in the Class content tab. Gated on the caller's `showGuideButtons`, since only
         * the XPS can tell a guides-only class apart — GRIMSBY's content tags cannot (see the
         * note in `getButtons` above).
         */
        const guideButtons = isIltClassroomsXpsEnabled && !isDashboard && displayedShowGuideButtons && (
            <SuspenseLiveComponent
                experienceProviderName='iltclassrooms'
                componentName='GuideButtons'
                props={{
                    learningActivityId: course.id,
                    placement: 'header',
                    registrationStatus: course.registrationStatus,
                }}
                hideSuspenseFallback
            />
        );

        const enrollButton = shouldShowEnrollButton(course, user, features, props.orgId, userRequests) && (
            <Button
                variant='primary'
                onClick={() => enroll()}
                loading={loading}
                ariaLabel='Enroll'
                disabled={course.schedule.seatsRemaining <= 0}
                data-testid='button-enroll'
            >
                Enroll{!loading || 'ing'}
            </Button>
        );

        const withdrawButton = shouldShowWithdrawButton(course, user, features, props.orgId, userRequests) && (
            <Button variant='link' onClick={() => withdraw()} ariaLabel='Withdraw' loading={loading} data-testid='button-withdraw'>
                Withdraw{!loading || 'ing'}
            </Button>
        );

        const waitlistButton = shouldShowWaitlistButton(course, user, features, props.orgId, userRequests) && (
            <Button variant='normal' onClick={() => waitlist()} loading={loading} ariaLabel='Waitlist' data-testid='button-waitlist'>
                Waitlist{!loading || 'ing'}
            </Button>
        );

        const unwaitlistButton = shouldShowUnwaitlistButton(course, user, features, props.orgId, userRequests) && (
            <Button variant='link' onClick={() => unwaitlist()} ariaLabel='Withdraw' loading={loading} data-testid='button-unwaitlist'>
                Unwaitlist{!loading || 'ing'}
            </Button>
        );

        const evaluationButton = shouldShowEvaluationButton(course) && (
            <Button
                href={course.evaluationUrl}
                iconAlign='right'
                iconName='external'
                ariaLabel='Evaluation Survey'
                data-testid='button-evaluation'
                target='_blank'
                onClick={() => trackUserEvent(TELEMETRY_EVENT_TYPES.LINK_CLICK, telemetry.evaluationSurvey)}
            >
                Evaluation Survey
            </Button>
        );

        const certificateButton = shouldShowCertificateButton(course) && (
            <Button
                onClick={() => downloadCertificate()}
                variant='primary'
                ariaLabel='Certificate'
                data-testid='button-certificate'
                loading={loadingCertificate}
            >
                {loadingCertificate ? 'Downloading' : 'Certificate'}
            </Button>
        );

        const joinInstruction = shouldShowJoinInstruction(course, features) && (
            <h5>To join the virtual class, please reach out to your administrator.</h5>
        );

        const requestAccessButton = shouldShowRequestAccessButton(course, user, features, props.orgId, userRequests) && (
            <SpaceBetween size='xs' direction='vertical'>
                <Box color='text-body-secondary' data-testid='request-access-disclaimer'>
                    {
                        "By requesting access to this organization's Classroom Training, the organization will be able to view your name and email address for registration purposes."
                    }
                </Box>
                <Button
                    variant='primary'
                    onClick={() => requestAccess()}
                    loading={loading}
                    ariaLabel='Request Access'
                    data-testid='button-request-access'
                >
                    Request{!loading || 'ing'} Access
                </Button>
            </SpaceBetween>
        );

        const requestAccessPending = shouldShowRequestAccessPending(course, user, features, props.orgId, userRequests) && (
            <Button variant='primary' disabled data-testid='text-request-access-pending'>
                Access requested
            </Button>
        );

        const requestAccessApproved = shouldShowRequestAccessApproved(course, user, features, props.orgId, userRequests) && (
            <Button variant='primary' disabled data-testid='text-request-access-approved'>
                Access request approved
            </Button>
        );

        const requestAccessRejected = shouldShowRequestAccessRejected(course, user, features, props.orgId, userRequests) && (
            <Button variant='primary' disabled data-testid='text-request-access-rejected'>
                Access request rejected
            </Button>
        );

        // Handle special cases
        if (course.courseStatus === LearningActivityStatus.CANCELED) {
            return null;
        }

        // Collect content buttons that should be shown with keys
        const contentButtons = [
            { key: 'enroll', component: enrollButton },
            { key: 'withdraw', component: withdrawButton },
            { key: 'waitlist', component: waitlistButton },
            { key: 'unwaitlist', component: unwaitlistButton },
            { key: 'certificate', component: certificateButton },
            { key: 'classroom', component: classroomButton },
            { key: 'joinTraining', component: joinTrainingButton },
            { key: 'evaluation', component: evaluationButton },
            { key: 'requestAccess', component: requestAccessButton },
            { key: 'requestAccessPending', component: requestAccessPending },
            { key: 'requestAccessApproved', component: requestAccessApproved },
            { key: 'requestAccessRejected', component: requestAccessRejected },
            { key: 'joinInstruction', component: joinInstruction },
            // Last: the one button that arrives only after the XPS resolves the classroom. The rest of
            // the row renders straight away rather than waiting on it.
            { key: 'guideButtons', component: guideButtons },
        ]
            .filter((item) => item.component)
            .map((item) => <React.Fragment key={item.key}>{item.component}</React.Fragment>);

        // If we have multiple content buttons, wrap them in SpaceBetween
        if (contentButtons.length > 1) {
            return (
                <SpaceBetween size='s' direction='horizontal'>
                    {contentButtons}
                </SpaceBetween>
            );
        } else {
            return contentButtons[0] || null;
        }
    };

    return (
        <Box>
            <SpaceBetween size='xs' direction='horizontal'>
                <h4 className='header' aria-label='Class schedule'>
                    Class schedule
                </h4>
                {getStatusIndicator(displayedCourse)}
            </SpaceBetween>
            {props.course?.location && props.showLocationHeading && (
                <SpaceBetween size='xs' direction='horizontal'>
                    <h4 className='header' aria-label='Location'>
                        Location
                    </h4>
                    {props.course.location}
                </SpaceBetween>
            )}
            <ClassSchedule schedule={displayedCourse.schedule} registrationStatus={displayedCourse.registrationStatus} />
            <Box className='buttons' float='right'>
                {getButtons(displayedCourse)}
            </Box>
        </Box>
    );
};
